import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { batch, useDispatch } from 'react-redux';

import Modal from '../Modal/Modal';
import Text from '../Text/Text';
import Alert from '../Alert/Alert';
import alertType from '../../common/alertType';
import Button from '../Button/Button';
import { METHOD_POST } from '../../constants/api';
import { set } from '../../utils/request';
import getMyProfile from '../../thunks/profile/getMyProfile';
import { resetApplicationState } from '../../actions/application/application';

const FaceLoginModal = ({
  visible,
  onClose,
  onAuthenticated,
  title,
  subTitle,
}) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const [faceLoginError, setFaceLoginError] = useState(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  async function stopCamera() {
    const currentStream = mediaStreamRef.current;

    if (!currentStream) {
      return;
    }

    currentStream.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }

  function clearModalState() {
    setFaceLoginError(null);
    setCapturedPreview(null);
    setIsAuthenticating(false);
    setIsCameraLoading(false);
  }

  async function closeModal() {
    await stopCamera();
    clearModalState();
    onClose();
  }

  function getReadableCameraError(error) {
    const message = error?.message || String(error || '');

    if (/permission|denied|notallowed/i.test(message)) {
      return 'Allow camera access in the browser and try again';
    }

    if (/secure|https/i.test(message)) {
      return 'Camera access requires a secure origin (HTTPS or localhost)';
    }

    if (/camera|device|video/i.test(message)) {
      return 'Unable to start the camera. Try another camera, another browser, or upload a photo.';
    }

    return message || 'Camera access is unavailable';
  }

  async function startCamera() {
    if (!visible || mediaStreamRef.current || !videoRef.current) {
      return;
    }

    setFaceLoginError(null);
    setIsCameraLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
        },
        audio: false,
      });

      mediaStreamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    } catch (error) {
      setFaceLoginError(getReadableCameraError(error));
    } finally {
      setIsCameraLoading(false);
    }
  }

  async function handleAuthenticated(response) {
    localStorage.setItem('token', JSON.stringify({ token: response.token }));

    batch(() => {
      dispatch(resetApplicationState());
      dispatch(getMyProfile());
    });

    await stopCamera();
    clearModalState();
    onClose();
    onAuthenticated();
  }

  async function authenticateFace(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);

    setIsAuthenticating(true);
    setFaceLoginError(null);

    try {
      const response = await set(METHOD_POST, '/login/face', formData);
      await handleAuthenticated(response);
    } catch (error) {
      setFaceLoginError(error.message);
    } finally {
      setIsAuthenticating(false);
    }
  }

  async function captureFrame() {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      setFaceLoginError('Camera is not ready yet');
      return;
    }

    canvasElement.width = videoElement.videoWidth;
    canvasElement.height = videoElement.videoHeight;

    const context = canvasElement.getContext('2d');
    context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);
    setCapturedPreview(canvasElement.toDataURL('image/jpeg', 0.92));

    canvasElement.toBlob(async (blob) => {
      if (!blob) {
        setFaceLoginError('Unable to capture the current frame');
        return;
      }

      const imageFile = new File([blob], 'face-login.jpg', { type: 'image/jpeg' });
      await authenticateFace(imageFile);
    }, 'image/jpeg', 0.92);
  }

  async function handleFileChange(event) {
    const inputElement = event.currentTarget;
    const imageFile = inputElement.files?.[0];

    if (!imageFile) {
      return;
    }

    setCapturedPreview(URL.createObjectURL(imageFile));
    await authenticateFace(imageFile);
    inputElement.value = '';
  }

  useEffect(() => {
    if (visible) {
      startCamera();
      return undefined;
    }

    stopCamera();
    clearModalState();
    return undefined;
  }, [visible]);

  return (
    <Modal
      visible={visible}
      title={title}
      subTitle={subTitle}
      // eslint-disable-next-line react/jsx-no-bind
      toggleVisible={closeModal}
      showFooter={false}
    >
      <div className="flex flex-col gap-[16px]">
        <div className="min-h-[280px] rounded-[8px] overflow-hidden bg-background-main relative">
          {capturedPreview ? (
            <img
              src={capturedPreview}
              alt="Face login preview"
              className="w-full h-[280px] object-cover"
            />
          ) : (
            <video
              ref={videoRef}
              className="w-full h-[280px] object-cover"
              autoPlay
              muted
              playsInline
            />
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg"
          className="hidden"
          onChange={handleFileChange}
        />

        <Text
          text="Look into the camera and capture a clear photo, or upload one manually."
          muted
          styleContainer="justify-center"
        />

        <div className="flex justify-center gap-[12px] flex-wrap">
          <Button
            text={capturedPreview ? 'Try Again' : 'Capture and Login'}
            disabled={isCameraLoading || isAuthenticating}
            onClick={() => {
              if (capturedPreview) {
                setCapturedPreview(null);
                setFaceLoginError(null);
                return;
              }

              captureFrame();
            }}
          />
          <Button
            text="Upload Photo"
            disabled={isAuthenticating}
            onClick={() => fileInputRef.current?.click()}
          />
        </div>

        {isCameraLoading ? (
          <Text
            text="Starting camera..."
            muted
            styleContainer="justify-center"
          />
        ) : null}

        {faceLoginError ? (
          <Alert
            text={faceLoginError}
            type={alertType.warning}
            actionText="Retry camera"
            onAction={() => {
              setCapturedPreview(null);
              startCamera();
            }}
          />
        ) : null}
      </div>
    </Modal>
  );
};

FaceLoginModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onAuthenticated: PropTypes.func,
  title: PropTypes.string,
  subTitle: PropTypes.string,
};

FaceLoginModal.defaultProps = {
  visible: false,
  onClose: () => {},
  onAuthenticated: () => {},
  title: 'Login with Amazon Rekognition',
  subTitle: 'Capture a selfie or upload a recent face photo to continue.',
};

export default FaceLoginModal;
